import React, { useState } from 'react';
import { calendarData } from '../data/calendarData';

const FullCalendarPage: React.FC = () => {
  const [viewMode, setViewMode] = useState<'grid' | 'monthly'>('grid'); // Default to grid view
  const [currentMonth, setCurrentMonth] = useState<number>(1); // For monthly view
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const monthNames = [
    '一月', '二月', '三月', '四月', '五月', '六月',
    '七月', '八月', '九月', '十月', '十一月', '十二月'
  ];

  // Get today's date for marking
  const today = new Date();
  const todayString = `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`;

  // Get days for current month
  const getDaysInMonth = (month: number) => {
    return calendarData.filter(day => 
      parseInt(day.date.split('-')[1]) === month
    );
  };

  // Get a background image for the month
  const getBackgroundForMonth = (month: number) => {
    // Use the first day of the month to get its photo
    const monthData = calendarData.find(day =>
      parseInt(day.date.split('-')[1]) === month
    );
    // 确保有背景图片，使用 Picsum 作为后备
    return monthData?.photo || `https://picsum.photos/seed/month-${month}/1600/900`;
  };

  // Navigation functions for monthly view
  const goToPreviousMonth = () => {
    setCurrentMonth(prev => prev > 1 ? prev - 1 : 12);
  };

  const goToNextMonth = () => {
    setCurrentMonth(prev => prev < 12 ? prev + 1 : 1);
  };

  // Function to handle day click and navigate to specific date
  const handleDayClick = (date: string) => {
    // Update the parent component's state to show the calendar for the selected date
    // We'll dispatch a custom event to notify the parent component
    const event = new CustomEvent('navigateToDate', { detail: { date } });
    window.dispatchEvent(event);
  };

  // Monthly view component
  const MonthlyView = () => {
    const daysInCurrentMonth = getDaysInMonth(currentMonth);
    const backgroundPhoto = getBackgroundForMonth(currentMonth);

    return (
      <div
        style={{
          minHeight: '100vh',
          backgroundImage: `url(${backgroundPhoto})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '20px',
          fontFamily: '"KaiTi", "楷体", serif',
          position: 'relative'
        }}
      >
        {/* Overlay for softening the background */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 0
          }}
        ></div>

        {/* Position Chihuahua in top-left corner */}
        <div style={{ position: 'absolute', top: '20px', left: '20px', zIndex: 100 }}>
          <svg width="60" height="60" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Body */}
            <path
              d="M30 50C30 50 40 60 50 55C60 60 70 50 70 50C70 50 65 40 60 35C55 30 45 30 40 35C35 40 30 50 30 50Z"
              fill="white"
              stroke="#ccc"
              strokeWidth="1"
            />
            {/* Head */}
            <path
              d="M60 35C60 30 65 25 70 30C75 25 80 30 80 35C80 40 75 45 70 40C65 45 60 40 60 35Z"
              fill="white"
              stroke="#ccc"
              strokeWidth="1"
            />
            {/* Ears */}
            <path
              d="M75 25L78 20L82 25Z"
              fill="white"
              stroke="#ccc"
              strokeWidth="1"
            />
            <path
              d="M65 25L62 20L58 25Z"
              fill="white"
              stroke="#ccc"
              strokeWidth="1"
            />
            {/* Eyes */}
            <circle cx="67" cy="33" r="1.5" fill="#333" />
            <circle cx="73" cy="33" r="1.5" fill="#333" />
            {/* Nose */}
            <ellipse cx="70" cy="37" rx="2" ry="1" fill="#333" />
            {/* Tail */}
            <path
              d="M30 50C25 45 20 47 18 52C16 57 20 60 25 58"
              fill="none"
              stroke="#ccc"
              strokeWidth="1.5"
            />
          </svg>
        </div>

        <div style={{ position: 'relative', zIndex: 1, padding: '20px' }}>
          <div className="text-center mb-4">
            <h1 className="display-4" style={{
              color: '#2E7D32',
              fontFamily: '"LiSu", "隶书", "KaiTi", "楷体", serif',
              textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
            }}>
              {monthNames[currentMonth - 1]} ({currentMonth}月)
            </h1>
            <p className="lead" style={{ color: '#555', fontFamily: '"KaiTi", "楷体", serif' }}>
              2026年完整日历 - 感受四季变化的诗意
            </p>

            {/* Month Navigation */}
            <div className="d-flex justify-content-center align-items-center my-4">
              <button
                className="btn btn-outline-secondary me-3"
                onClick={goToPreviousMonth}
                style={{
                  borderRadius: '20px',
                  fontFamily: '"KaiTi", "楷体", serif'
                }}
              >
                ← 上一月
              </button>

              <div className="mx-3">
                {months.map(month => (
                  <button
                    key={month}
                    className={`btn mx-1 ${currentMonth === month ? 'btn-success' : 'btn-outline-success'}`}
                    onClick={() => setCurrentMonth(month)}
                    style={{
                      borderRadius: '50%',
                      width: '36px',
                      height: '36px',
                      padding: '0',
                      fontFamily: '"KaiTi", "楷体", serif',
                      fontSize: '0.8rem'
                    }}
                  >
                    {month}
                  </button>
                ))}
              </div>

              <button
                className="btn btn-outline-secondary ms-3"
                onClick={goToNextMonth}
                style={{
                  borderRadius: '20px',
                  fontFamily: '"KaiTi", "楷体", serif'
                }}
              >
                下一月 →
              </button>
            </div>

            {/* Back to Grid View Button */}
            <button
              className="btn btn-outline-success mt-3"
              onClick={() => setViewMode('grid')}
              style={{
                borderRadius: '20px',
                fontFamily: '"KaiTi", "楷体", serif'
              }}
            >
              返回全年视图
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="container">
            <div className="row">
              {daysInCurrentMonth.map(day => {
                const isHoliday = day.isHoliday;
                const isToday = day.date === todayString;
                
                return (
                  <div key={day.date} className="col-md-3 col-sm-4 col-6 mb-3">
                    <div
                      className="card h-100"
                      style={{
                        border: isHoliday ? '2px solid #FF5722' : isToday ? '2px solid #4CAF50' : '1px solid #ddd',
                        backgroundColor: isToday
                          ? 'rgba(76, 175, 80, 0.2)'
                          : isHoliday
                            ? 'rgba(255, 87, 34, 0.1)'
                            : 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(5px)',
                        position: 'relative',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleDayClick(day.date)}
                    >
                      {isToday && (
                        <div 
                          style={{
                            position: 'absolute',
                            top: '5px',
                            right: '5px',
                            backgroundColor: '#4CAF50',
                            color: 'white',
                            fontSize: '0.6rem',
                            padding: '2px 5px',
                            borderRadius: '10px',
                            zIndex: 2
                          }}
                        >
                          今天
                        </div>
                      )}
                      
                      <div className="card-body d-flex flex-column" style={{ padding: '0.75rem', minHeight: '120px' }}>
                        <div className="d-flex justify-content-between align-items-start">
                          <h6 
                            className="card-title" 
                            style={{ 
                              color: isHoliday ? '#FF5722' : '#2E7D32',
                              fontWeight: isHoliday ? 'bold' : 'normal',
                              fontSize: '0.9rem',
                              marginBottom: '0.5rem'
                            }}
                          >
                            {new Date(day.date).getDate()}日
                          </h6>
                          {isHoliday && (
                            <span 
                              className="badge" 
                              style={{ 
                                backgroundColor: '#FF5722', 
                                fontSize: '0.6rem',
                                alignSelf: 'flex-start'
                              }}
                            >
                              {day.holidayName}
                            </span>
                          )}
                        </div>
                        
                        <p 
                          className="card-text flex-grow-1" 
                          style={{ 
                            fontSize: '0.75rem', 
                            color: '#333',
                            margin: '5px 0',
                            minHeight: '3rem',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical'
                          }}
                          title={day.blessing}
                        >
                          {day.blessing.length > 25 
                            ? `${day.blessing.substring(0, 25)}...` 
                            : day.blessing}
                        </p>
                        
                        <small 
                          style={{ 
                            color: '#666', 
                            fontSize: '0.7rem',
                            fontStyle: 'italic',
                            marginTop: 'auto'
                          }}
                          title={day.poem}
                        >
                          {day.poem?.substring(0, 15)}...
                        </small>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Month indicator */}
          <div className="text-center mt-4" style={{ color: '#555', fontFamily: '"KaiTi", "楷体", serif' }}>
            <p>2026年 • 第 {currentMonth} 月</p>
          </div>
        </div>
      </div>
    );
  };

  // Grid view component
  const GridView = () => {
    // Function to handle day click and navigate to specific date
    const handleDayClick = (date: string) => {
      // Update the parent component's state to show the calendar for the selected date
      // We'll dispatch a custom event to notify the parent component
      const event = new CustomEvent('navigateToDate', { detail: { date } });
      window.dispatchEvent(event);
    };

    return (
      <div style={{
        padding: '20px',
        backgroundColor: '#f8f9fa',
        minHeight: '100vh',
        fontFamily: '"KaiTi", "楷体", serif'
      }}>
        <div className="text-center mb-4">
          <h1 className="display-4" style={{ 
            color: '#2E7D32', 
            fontFamily: '"LiSu", "隶书", "KaiTi", "楷体", serif',
            textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
          }}>
            2026年完整日历
          </h1>
          <p className="lead" style={{ color: '#555', fontFamily: '"KaiTi", "楷体", serif' }}>
            感受四季变化的诗意
          </p>
          
          {/* View Mode Toggle */}
          <div className="btn-group mb-4" role="group">
            <button
              type="button"
              className={`btn ${viewMode === 'grid' ? 'btn-success' : 'btn-outline-success'}`}
              onClick={() => setViewMode('grid')}
              style={{
                fontFamily: '"KaiTi", "楷体", serif',
                borderRadius: '20px 0 0 20px'
              }}
            >
              全年视图
            </button>
            <button
              type="button"
              className={`btn ${viewMode === 'monthly' ? 'btn-success' : 'btn-outline-success'}`}
              onClick={() => setViewMode('monthly')}
              style={{
                fontFamily: '"KaiTi", "楷体", serif',
                borderRadius: '0 20px 20px 0'
              }}
            >
              月份视图
            </button>
          </div>
        </div>

        <div className="container">
          {months.map(month => (
            <div key={month} className="mb-5" style={{ breakInside: 'avoid' }}>
              <h2 style={{ 
                color: '#2E7D32', 
                borderBottom: '2px solid #2E7D32', 
                paddingBottom: '10px',
                fontFamily: '"LiSu", "隶书", "KaiTi", "楷体", serif'
              }}>
                {monthNames[month - 1]} ({month}月)
              </h2>
              
              <div className="row">
                {getDaysInMonth(month).map(day => {
                  const isHoliday = day.isHoliday;
                  const isToday = day.date === todayString;
                  
                  return (
                    <div key={day.date} className="col-md-3 col-sm-4 col-6 mb-3">
                      <div
                        className="card h-100"
                        style={{
                          border: isHoliday ? '2px solid #FF5722' : isToday ? '2px solid #4CAF50' : '1px solid #ddd',
                          backgroundColor: isToday
                            ? 'rgba(76, 175, 80, 0.2)'
                            : isHoliday
                              ? 'rgba(255, 87, 34, 0.1)'
                              : 'white',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleDayClick(day.date)}
                      >
                        {isToday && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '5px',
                              right: '5px',
                              backgroundColor: '#4CAF50',
                              color: 'white',
                              fontSize: '0.6rem',
                              padding: '2px 5px',
                              borderRadius: '10px',
                              zIndex: 2
                            }}
                          >
                            今天
                          </div>
                        )}

                        <div className="card-body d-flex flex-column" style={{ padding: '0.75rem' }}>
                          <h6
                            className="card-title"
                            style={{
                              color: isHoliday ? '#FF5722' : '#2E7D32',
                              fontWeight: isHoliday ? 'bold' : 'normal',
                              fontSize: '0.9rem'
                            }}
                          >
                            {new Date(day.date).getDate()}日
                            {isHoliday && (
                              <span className="badge bg-danger" style={{ fontSize: '0.6rem', marginLeft: '4px' }}>
                                {day.holidayName}
                              </span>
                            )}
                          </h6>
                          <p
                            className="card-text"
                            style={{
                              fontSize: '0.75rem',
                              color: '#666',
                              margin: '5px 0',
                              minHeight: '3rem',
                              overflow: 'hidden'
                            }}
                            title={day.blessing}
                          >
                            {day.blessing.length > 20
                              ? `${day.blessing.substring(0, 20)}...`
                              : day.blessing}
                          </p>
                          <small
                            style={{
                              color: '#888',
                              fontSize: '0.7rem',
                              marginTop: 'auto'
                            }}
                          >
                            {day.poem?.substring(0, 15)}...
                          </small>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render based on view mode
  return viewMode === 'monthly' ? <MonthlyView /> : <GridView />;
};

export default FullCalendarPage;